import { useState, type FormEvent } from 'react';
import { api, errorText } from '../../api';
import { fmtDate } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';

// Papierkorb (Admin, DSGVO Art. 17): Was Mandant oder Kanzlei entfernt haben, bleibt mit der Datei aufbewahrt (GoBD).
// Falsch Hochgeladenes (private Fotos, Unterlagen eines anderen Mandanten …) löscht der Admin hier endgültig, mit Grund
// im Protokoll. Alles andere löscht die App nach Ablauf der Aufbewahrungsfrist.

interface Item {
  art: string;
  id: string;
  title: string;
  removedAt: string;
  removedBy: string | null;
  // war gebucht: bleibt bis zum Ende der Aufbewahrungsfrist
  keep?: true;
}

export function Papierkorb({ clientId }: { clientId: string }) {
  const res = useApi<{ items: Item[] }>(`/api/kanzlei/clients/${clientId}/papierkorb`, {
    live: ['belege', 'lohn', 'anfragen', 'fristen', 'guv', 'docs'],
    clientId,
  });
  const [ask, setAsk] = useState<string | null>(null);
  const items = res.data?.items;
  return (
    <section className="card" aria-labelledby="pk-h">
      <div className="ch">
        <h3 id="pk-h">Papierkorb</h3>
      </div>
      <p className="note">
        Was beim Mandanten entfernt wurde, bleibt mit der Datei aufbewahrt. Falsch Hochgeladenes, z. B. private Fotos oder
        Unterlagen eines anderen Mandanten, hier endgültig löschen. Alles andere löscht die App nach Ablauf der
        Aufbewahrungsfrist.
      </p>
      {!items ? (
        res.error ? (
          <p className="note">{res.error}</p>
        ) : (
          <div className="spinner" aria-label="Lädt" />
        )
      ) : !items.length ? (
        <p className="note">Leer.</p>
      ) : (
        <ul className="pk">
          {items.map((x) =>
            ask === x.id ? (
              <li key={x.id}>
                <DeleteForm item={x} onCancel={() => setAsk(null)} onDone={res.reload} />
              </li>
            ) : (
              <li key={x.id}>
                <span>
                  {x.title}
                  <span className="sub">
                    entfernt am {fmtDate(x.removedAt)}
                    {x.removedBy ? ` von ${x.removedBy}` : ''}
                  </span>
                </span>
                {x.keep ? (
                  <span className="note">war gebucht, bleibt bis zum Ende der Frist</span>
                ) : (
                  <button type="button" className="btn sm" onClick={() => setAsk(x.id)}>
                    Endgültig löschen
                  </button>
                )}
              </li>
            ),
          )}
        </ul>
      )}
    </section>
  );
}

function DeleteForm({ item, onCancel, onDone }: { item: Item; onCancel(): void; onDone(): Promise<void> }) {
  const toast = useToast();
  const [grund, setGrund] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (grund.trim().length < 3) {
      setError('Bitte einen Grund angeben.');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/api/kanzlei/papierkorb/${item.art}/${item.id}/loeschen`, { grund: grund.trim() });
      toast(`${item.title} endgültig gelöscht`);
      await onDone();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  const id = `pk-${item.id}`;
  return (
    <form className="form pk-ask" onSubmit={submit} noValidate>
      <b>{item.title} endgültig löschen? Die Datei ist danach weg.</b>
      <label htmlFor={id}>
        Grund (steht im Protokoll)
        <input
          id={id}
          value={grund}
          onChange={(e) => {
            setGrund(e.target.value);
            setError(null);
          }}
          placeholder="z. B. privates Foto, falsch hochgeladen"
          maxLength={300}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-err` : undefined}
          autoFocus
        />
      </label>
      {error && (
        <span className="pk-err" id={`${id}-err`} role="alert">
          {error}
        </span>
      )}
      <span className="btnrow">
        <button type="submit" className="btn sm dark" disabled={busy}>
          Ja, endgültig löschen
        </button>
        <button type="button" className="btn sm" onClick={onCancel}>
          Abbrechen
        </button>
      </span>
    </form>
  );
}
